import { Capacitor } from '@capacitor/core';
import type { TFunction } from 'i18next';
import { Download, Mail, FileText, CreditCard, Receipt, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, BarChart2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { localizedPackName } from '../components/CreditSystem/creditCheckoutUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import { CREDIT_PACKS, getPackById, getPackGrantCredits, transactionAmountToRupees } from '../config/creditPacks';
import { supabase } from '../lib/supabase';
import { getUserInvoices, downloadInvoicePDF, sendInvoiceEmail } from '../services/invoiceService';
import type { Invoice } from '../services/invoiceService';
import { getUsageSummary } from '../services/creditLedgerService';
import type { UsageSummaryRow } from '../services/creditLedgerService';
import { formatPriceForLocale, getDisplayCurrencyCode } from '../utils/localePrice';

interface Transaction {
  id: string;
  order_id: string;
  amount: number;
  credits_amount: number;
  status: string;
  type: string;
  payment_gateway: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown> | null;
}

interface PaymentOrder {
  order_id: string;
  amount: number;
  status: string;
  created_at: string;
  payment_method: string;
  credits: number;
}

const PACK_NAME_TO_ID: Record<string, string> = {
  monthly: 'pack-monthly',
  quarterly: 'pack-quarterly',
  'half-yearly': 'pack-halfyearly',
  'half yearly': 'pack-halfyearly',
  'day pass': 'pack-daypass',
  weekly: 'pack-weekly',
};

function resolvePackIdFromDescription(description: string): string | null {
  const lower = description.toLowerCase();
  for (const pack of CREDIT_PACKS) {
    if (lower.includes(pack.id) || lower.includes(pack.name.toLowerCase())) {
      return pack.id;
    }
  }
  for (const [name, id] of Object.entries(PACK_NAME_TO_ID)) {
    if (lower.includes(name)) return id;
  }
  return null;
}

function localizedPackPeriod(packId: string, t: TFunction): string {
  if (packId === 'pack-monthly') return t('views.pricing.perMonth', 'per month');
  if (packId === 'pack-quarterly') {
    return t('views.pricing.per3Months', { defaultValue: '/ 3 months' }).replace(/^\//, '').trim();
  }
  if (packId === 'pack-halfyearly') {
    return t('views.pricing.per6Months', { defaultValue: '/ 6 months' }).replace(/^\//, '').trim();
  }
  return '';
}

function localizedInvoiceDescription(description: string, t: TFunction): string {
  const packId = resolvePackIdFromDescription(description);
  if (packId) {
    const pack = getPackById(packId);
    if (pack) {
      return t('dashboard.paymentHistory.planLineItem', {
        plan: localizedPackName(packId, t),
        credits: getPackGrantCredits(pack),
        period: localizedPackPeriod(packId, t),
        defaultValue: description,
      });
    }
  }
  const creditsMatch = description.match(/(\d+)\s*credits/i);
  if (creditsMatch) {
    return t('dashboard.paymentHistory.planLineItemFallback', {
      credits: creditsMatch[1],
      defaultValue: description,
    });
  }
  return description;
}

function categoryLabel(category: string, t: TFunction): string {
  const key = `dashboard.paymentHistory.categories.${category}`;
  const translated = t(key);
  return translated !== key ? translated : category;
}

function operationLabel(operation: string, t: TFunction): string {
  const key = `dashboard.paymentHistory.operations.${operation}`;
  const translated = t(key);
  return translated !== key ? translated : operation.replace(/_/g, ' ');
}

function transactionTypeLabel(type: string | undefined, t: TFunction): string {
  const normalized = (type || 'credit').toLowerCase();
  if (normalized === 'purchase') {
    return t('dashboard.paymentHistory.typePurchase', 'purchase');
  }
  return t('dashboard.paymentHistory.typeCredit', 'credit');
}

const PaymentHistoryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  type InvoiceLang = 'en' | 'pt' | 'es' | 'ru';
  const invoiceLang: InvoiceLang = (() => {
    const l = i18n.language?.split('-')[0]?.toLowerCase();
    if (l === 'pt') return 'pt';
    if (l === 'es') return 'es';
    if (l === 'ru') return 'ru';
    return 'en';
  })();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [usageRows, setUsageRows] = useState<UsageSummaryRow[]>([]);
  const [usagePeriod, setUsagePeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'invoices' | 'transactions' | 'orders' | 'usage'>('invoices');
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null);
  const [emailingInvoice, setEmailingInvoice] = useState<string | null>(null);
  
  // Notification state
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);

  const handleTabChange = (tab: 'invoices' | 'transactions' | 'orders' | 'usage') => {
    setActiveTab(tab);
    setSortColumn(''); // Reset sorting when changing tabs
    setSortOrder('asc');
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing records per page
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  // Fetch usage whenever tab switches to 'usage' or period changes
  useEffect(() => {
    if (activeTab !== 'usage') return;
    const days = usagePeriod === '7d' ? 7 : usagePeriod === '90d' ? 90 : 30;
    const from = new Date(Date.now() - days * 86400_000);
    getUsageSummary(from).then(setUsageRows);
  }, [activeTab, usagePeriod]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchPaymentHistory = async () => {
    console.log('🔄 [PaymentHistory] Starting fetch...');
    setLoading(true);
    try {
      // Get current user
      console.log('🔄 [PaymentHistory] Getting user...');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('❌ [PaymentHistory] Error getting user:', userError);
        setLoading(false);
        return;
      }
      
      console.log('✅ [PaymentHistory] User found:', user.id);

      // Fetch invoices (already filtered by user in getUserInvoices)
      console.log('🔄 [PaymentHistory] Fetching invoices...');
      const invoicesData = await getUserInvoices();
      console.log('✅ [PaymentHistory] Invoices fetched:', invoicesData.length);
      setInvoices(invoicesData);

      // Fetch transactions - FILTER BY CURRENT USER ONLY
      console.log('🔄 [PaymentHistory] Fetching transactions for user:', user.id);
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id) // 🔒 CRITICAL: Only fetch current user's transactions
        .order('created_at', { ascending: false });
      
      if (transactionsError) {
        console.error('❌ [PaymentHistory] Error fetching transactions:', transactionsError);
        // Still continue even if transactions fail - just show empty
        setTransactions([]);
        setOrders([]);
      } else {
        console.log('✅ [PaymentHistory] Transactions fetched:', transactionsData?.length || 0);
        
        if (transactionsData) {
          setTransactions(transactionsData);
          
          // Extract orders from transactions (already filtered by user)
          const ordersMap = new Map<string, PaymentOrder>();
          transactionsData.forEach(txn => {
            if (txn.order_id && !ordersMap.has(txn.order_id)) {
              ordersMap.set(txn.order_id, {
                order_id: txn.order_id,
                amount: txn.amount || 0,
                status: txn.status,
                created_at: txn.created_at,
                payment_method: txn.payment_gateway || 'cashfree',
                credits: txn.credits_amount || 0,
              });
            }
          });
          setOrders(Array.from(ordersMap.values()));
          console.log('✅ [PaymentHistory] Orders extracted:', ordersMap.size);
        }
      }
      
      console.log('✅ [PaymentHistory] Fetch complete!');
    } catch (error) {
      console.error('❌ [PaymentHistory] Unexpected error:', error);
      // Set empty data to show UI even on error
      setTransactions([]);
      setOrders([]);
    } finally {
      console.log('🏁 [PaymentHistory] Setting loading to false');
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    try {
      setDownloadingInvoice(invoice.id);
      await downloadInvoicePDF(invoice, invoiceLang);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(t('dashboard.paymentHistory.failedDownload', 'Failed to download invoice. Please try again.'));
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleEmailInvoice = async (invoice: Invoice) => {
    try {
      setEmailingInvoice(invoice.id);
      const success = await sendInvoiceEmail(invoice);
      if (success) {
        setNotification({
          type: 'success',
          message: t('dashboard.paymentHistory.emailSentTo', {
            invoice: invoice.invoice_number,
            email: invoice.customer_email,
            defaultValue: `Invoice ${invoice.invoice_number} has been sent to ${invoice.customer_email}`,
          }),
        });
      } else {
        setNotification({
          type: 'error',
          message: t('dashboard.paymentHistory.failedSendEmail', 'Failed to send email. Please try again.'),
        });
      }
    } catch (error) {
      console.error('Error emailing invoice:', error);
      setNotification({
        type: 'error',
        message: t('dashboard.paymentHistory.failedSendEmail', 'Failed to send email. Please try again.'),
      });
    } finally {
      setEmailingInvoice(null);
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ChevronDown className="w-4 h-4 t-muted opacity-50" />;
    }
    return sortOrder === 'asc' ? 
      <ChevronUp className="w-4 h-4 t-brand" /> : 
      <ChevronDown className="w-4 h-4 t-brand" />;
  };

  const sortData = <T extends Record<string, unknown>>(data: T[], column: string): T[] => {
    if (!column) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[column];
      let bVal = b[column];
      
      // Handle nested properties
      if (column.includes('.')) {
        const keys = column.split('.');
        aVal = keys.reduce((obj: Record<string, unknown> | undefined, key) => obj?.[key] as Record<string, unknown>, a as Record<string, unknown>);
        bVal = keys.reduce((obj: Record<string, unknown> | undefined, key) => obj?.[key] as Record<string, unknown>, b as Record<string, unknown>);
      }
      
      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      const comparison = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  /** Payment is either PAID or FAILED — never show PENDING. */
  const getStatusDisplayKey = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'paid') return 'dashboard.paymentHistory.statusCompleted';
    return 'dashboard.paymentHistory.statusFailed';
  };

  const getStatusChipClass = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'paid') return 'ds-chip-ok';
    if (s === 'pending' || s === 'processing') return 'ds-chip-warn';
    return 'ds-chip-danger';
  };

  /** Map order_id -> invoice_number for linking transactions to invoices */
  const orderIdToInvoiceNumber = useMemo(() => {
    const map = new Map<string, string>();
    invoices.forEach((inv) => map.set(inv.order_id, inv.invoice_number));
    return map;
  }, [invoices]);

  /** Display status: only PAID or FAILED. Pending/processing → failed. */
  const getTransactionDisplayStatus = (txn: Transaction): string => {
    const s = txn.status.toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'paid') return txn.status;
    return 'failed'; // pending, processing, failed, abandoned, etc.
  };

  /** Same for orders. */
  const getOrderDisplayStatus = (order: PaymentOrder): string => {
    const s = order.status.toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'paid') return order.status;
    return 'failed';
  };

  /** Invoices: use credit_transaction status only. Failed payment = never PAID. */
  const getEffectiveInvoiceStatus = (invoice: Invoice): string => {
    const txn = transactions.find((t) => t.order_id === invoice.order_id);
    const status = txn ? txn.status : invoice.payment_status;
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'paid') return status;
    return 'failed';
  };

  /** Format date in user's local timezone (ISO from DB is UTC) */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone;
    return date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(tz && { timeZone: tz }),
    });
  };

  const formatCurrency = (amount: number) => {
    const rupees = transactionAmountToRupees(amount);
    return formatPriceForLocale(rupees, i18n.language);
  };

  const displayCurrencyCode = () => getDisplayCurrencyCode(i18n.language);

  /** Short, readable order reference instead of full order_xxx id */
  const formatOrderRef = (orderId: string) => {
    if (!orderId) return '—';
    const raw = orderId.replace(/^order_/i, '');
    const short = raw.length >= 6 ? raw.slice(-6) : raw;
    return t('dashboard.paymentHistory.orderRef', {
      short,
      defaultValue: `Ref #${short}`,
    });
  };

  /** Short transaction reference for display */
  const formatTxnRef = (id: string) => {
    if (!id) return '—';
    return id.length > 8 ? `…#${id.slice(0, 8)}` : id;
  };

  // Pagination helper
  const getPaginatedData = <T,>(data: T[]): T[] => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Render pagination bar – touch-friendly on mobile
  const renderPagination = (totalRecords: number) => {
    const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
    const start = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
    const end = Math.min(currentPage * recordsPerPage, totalRecords);

    return (
      <div className="px-3 sm:px-4 py-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3">
        <span className="t-meta order-2 sm:order-1">
          {t('dashboard.paymentHistory.showing', { start, end: totalRecords === 0 ? 0 : end, total: totalRecords })}
        </span>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 order-1 sm:order-2">
          <label className="flex items-center gap-2 t-meta">
            <span className="whitespace-nowrap">{t('dashboard.paymentHistory.itemsPerPage')}:</span>
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
              className="ds-select min-w-[4rem] min-h-[44px] touch-manipulation"
              aria-label={t('dashboard.paymentHistory.itemsPerPage')}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
              aria-label={t('dashboard.paymentHistory.prevPage', 'Previous page')}
            >
              <ChevronLeft className="w-5 h-5 sm:hidden" />
              <span className="hidden sm:inline">{t('dashboard.paymentHistory.prev')}</span>
            </button>
            <span className="t-meta t-num px-2 min-w-[4.5rem] text-center">
              {currentPage}/{totalPages}
            </span>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
              aria-label={t('dashboard.paymentHistory.nextPage', 'Next page')}
            >
              <ChevronRight className="w-5 h-5 sm:hidden" />
              <span className="hidden sm:inline">{t('dashboard.paymentHistory.next')}</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && Capacitor.isNativePlatform()) {
    // On native, the MobilePageSkeleton handles loading feedback — no popup needed
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  /* ── Native card view ─────────────────────────────────────────────── */
  if (Capacitor.isNativePlatform()) {
    const TAB_ITEMS: { key: 'invoices' | 'transactions' | 'orders'; label: string; count: number }[] = [
      { key: 'invoices',     label: t('dashboard.paymentHistory.invoices', 'Invoices'),     count: invoices.length },
      { key: 'transactions', label: t('dashboard.paymentHistory.transactions', 'Transactions'), count: transactions.length },
      { key: 'orders',       label: t('dashboard.paymentHistory.orders', 'Orders'),       count: orders.length },
    ];

    const emptyState = (label: string) => (
      <div className="ds-card-sm text-center py-12 px-6 t-muted">
        <Receipt size={36} className="mx-auto mb-3 opacity-40" />
        <p className="t-card-title">{t('dashboard.paymentHistory.nativeEmptyTitle', { label, defaultValue: `No ${label} yet` })}</p>
        <p className="t-meta mt-1">{t('dashboard.paymentHistory.nativeEmptyDesc', { label: label.toLowerCase(), defaultValue: `Your ${label.toLowerCase()} will appear here after a purchase.` })}</p>
      </div>
    );

    return (
      <div className="ds-page px-4 py-3 min-h-full" key={i18n.language}>
        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          {TAB_ITEMS.map(({ key, label, count }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleTabChange(key)}
                className={`ds-card-sm flex-1 flex flex-col items-center gap-0.5 px-1 py-2 touch-manipulation ${active ? 'ds-recessed-info' : ''}`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <span className="t-h3 t-num">
                  {count}
                </span>
                <span className="t-label">
                  {label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Invoices */}
        {activeTab === 'invoices' && (
          invoices.length === 0 ? emptyState(t('dashboard.paymentHistory.invoices', 'Invoices')) :
          invoices.map((inv) => {
            const effStatus = getEffectiveInvoiceStatus(inv);
            return (
              <div key={inv.id} className="ds-card-sm mb-2.5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="t-card-title mb-0.5">
                      {inv.invoice_number}
                    </p>
                    <p className="t-meta">
                      {formatOrderRef(inv.order_id)}
                    </p>
                  </div>
                  <span className={`ds-chip ${getStatusChipClass(effStatus)} capitalize`}>
                    {t(getStatusDisplayKey(effStatus))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="t-meta mb-0.5">
                      {formatDate(inv.created_at)}
                    </p>
                    <p className="t-card-title t-brand t-num">
                      {formatCurrency(inv.total_amount ?? inv.subtotal ?? 0)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoice(inv)}
                      disabled={!!downloadingInvoice}
                      className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label={t('dashboard.paymentHistory.download', 'Download')}
                    >
                      <Download size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEmailInvoice(inv)}
                      disabled={!!emailingInvoice}
                      className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label={t('dashboard.paymentHistory.email', 'Email')}
                    >
                      <Mail size={15} />
                    </button>
                  </div>
                </div>
                {inv.items && inv.items.length > 0 && (
                  <p className="t-meta mt-2 pt-2 border-t border-[color:var(--ds-border)]">
                    {t('dashboard.paymentHistory.itemsCount', {
                      count: inv.items.length,
                      defaultValue: `${inv.items.length} item(s)`,
                    })} · {inv.items.map((i) => localizedInvoiceDescription(i.description, t)).join(', ')}
                  </p>
                )}
              </div>
            );
          })
        )}

        {/* Transactions */}
        {activeTab === 'transactions' && (
          transactions.length === 0 ? emptyState(t('dashboard.paymentHistory.transactions', 'Transactions')) :
          transactions.map((txn) => {
            const dispStatus = getTransactionDisplayStatus(txn);
            return (
              <div key={txn.id} className="ds-card-sm mb-2.5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="t-card-title t-num mb-0.5">
                      {txn.credits_amount} {t('dashboard.paymentHistory.credits', 'Credits')}
                    </p>
                    <p className="t-meta">
                      {formatTxnRef(txn.id)} · {transactionTypeLabel(txn.type, t)}
                    </p>
                  </div>
                  <span className={`ds-chip ${getStatusChipClass(dispStatus)} capitalize`}>
                    {t(getStatusDisplayKey(dispStatus))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <p className="t-meta">
                    {formatDate(txn.created_at)}
                  </p>
                  <p className="t-card-title t-brand t-num">
                    {formatCurrency(txn.amount)}
                  </p>
                </div>
                {orderIdToInvoiceNumber.get(txn.order_id) && (
                  <p className="t-meta mt-1.5">
                    {t('dashboard.paymentHistory.invoiceLabel', {
                      number: orderIdToInvoiceNumber.get(txn.order_id),
                      defaultValue: `Invoice: ${orderIdToInvoiceNumber.get(txn.order_id)}`,
                    })}
                  </p>
                )}
              </div>
            );
          })
        )}

        {/* Orders */}
        {activeTab === 'orders' && (
          orders.length === 0 ? emptyState(t('dashboard.paymentHistory.orders', 'Orders')) :
          orders.map((ord) => {
            const dispStatus = getOrderDisplayStatus(ord);
            return (
              <div key={ord.order_id} className="ds-card-sm mb-2.5">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="t-card-title mb-0.5">
                      {formatOrderRef(ord.order_id)}
                    </p>
                    <p className="t-meta">
                      <span className="t-num">{ord.credits}</span> {t('dashboard.paymentHistory.credits', 'Credits').toLowerCase()} · {t('dashboard.paymentHistory.via', 'via')} {ord.payment_method}
                    </p>
                  </div>
                  <span className={`ds-chip ${getStatusChipClass(dispStatus)} capitalize`}>
                    {t(getStatusDisplayKey(dispStatus))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <p className="t-meta">
                    {formatDate(ord.created_at)}
                  </p>
                  <p className="t-card-title t-brand t-num">
                    {formatCurrency(ord.amount)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div className="ds-page min-h-screen px-3 py-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))] overflow-x-hidden" key={i18n.language}>
      <div className="max-w-7xl mx-auto min-w-0 w-full">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="t-page-title mb-1 sm:mb-2">{t('dashboard.paymentHistory.title')}</h1>
          <p className="t-body">{t('dashboard.paymentHistory.pageDesc', 'View all your invoices, transactions, and orders')}</p>
        </div>

        {/* Notification Banner */}
        {notification && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-start justify-between animate-slideDown ${
            notification.type === 'success'
              ? 'bg-green-500/10 border-green-500 text-green-400'
              : 'bg-red-500/10 border-red-500 text-red-400'
          }`}>
            <div className="flex items-center space-x-3">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <p className="text-sm font-medium break-words">{notification.message}</p>
            </div>
            <button
              type="button"
              onClick={() => setNotification(null)}
              className="ml-2 sm:ml-4 min-h-[44px] min-w-[44px] flex items-center justify-center -my-2 text-current opacity-70 hover:opacity-100 transition-opacity touch-manipulation"
              aria-label={t('dashboard.paymentHistory.dismiss', 'Dismiss')}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Tabs — scrollable on small screens; short labels on xs so all visible when possible */}
        <div className="flex mb-4 sm:mb-6 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-thin gap-0" style={{ WebkitOverflowScrolling: 'touch' }} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'invoices'}
            onClick={() => handleTabChange('invoices')}
            className={`flex items-center flex-shrink-0 min-h-[44px] px-3 sm:px-5 py-3 font-semibold transition-all whitespace-nowrap touch-manipulation border-b-2 ${
              activeTab === 'invoices'
                ? 't-brand border-[color:var(--ds-brand)]'
                : 'border-transparent t-muted hover:t-strong'
            }`}
          >
            <Receipt className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
            <span className="text-xs sm:text-base truncate max-w-[100px] xs:max-w-[140px] sm:max-w-none">{t('dashboard.paymentHistory.invoices')}</span>
            <span className="ml-1 t-num">({invoices.length})</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'transactions'}
            onClick={() => handleTabChange('transactions')}
            className={`flex items-center flex-shrink-0 min-h-[44px] px-3 sm:px-5 py-3 font-semibold transition-all whitespace-nowrap touch-manipulation border-b-2 ${
              activeTab === 'transactions'
                ? 't-brand border-[color:var(--ds-brand)]'
                : 'border-transparent t-muted hover:t-strong'
            }`}
          >
            <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
            <span className="text-xs sm:text-base truncate max-w-[100px] xs:max-w-[140px] sm:max-w-none">{t('dashboard.paymentHistory.transactions')}</span>
            <span className="ml-1 t-num">({transactions.length})</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'orders'}
            onClick={() => handleTabChange('orders')}
            className={`flex items-center flex-shrink-0 min-h-[44px] px-3 sm:px-5 py-3 font-semibold transition-all whitespace-nowrap touch-manipulation border-b-2 ${
              activeTab === 'orders'
                ? 't-brand border-[color:var(--ds-brand)]'
                : 'border-transparent t-muted hover:t-strong'
            }`}
          >
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
            <span className="text-xs sm:text-base truncate max-w-[100px] xs:max-w-[140px] sm:max-w-none">{t('dashboard.paymentHistory.orders')}</span>
            <span className="ml-1 t-num">({orders.length})</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'usage'}
            onClick={() => handleTabChange('usage')}
            className={`flex items-center flex-shrink-0 min-h-[44px] px-3 sm:px-5 py-3 font-semibold transition-all whitespace-nowrap touch-manipulation border-b-2 ${
              activeTab === 'usage'
                ? 't-brand border-[color:var(--ds-brand)]'
                : 'border-transparent t-muted hover:t-strong'
            }`}
          >
            <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
            <span className="text-xs sm:text-base truncate max-w-[100px] xs:max-w-[140px] sm:max-w-none">{t('dashboard.paymentHistory.usage', 'Usage')}</span>
          </button>
        </div>

        {/* Invoices — table only, no card border/bg to avoid dark strips */}
        {activeTab === 'invoices' && (
          <div className="rounded-xl overflow-hidden min-w-0">
            <p className="md:sr-only t-meta mb-2 px-0.5" aria-hidden="true">{t('dashboard.paymentHistory.swipeHint', 'Swipe table to see all columns')}</p>
            <div className="overflow-x-auto scrollbar-thin" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="ds-table min-w-[640px]">
                <thead>
                  <tr>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('sno')}
                      >
                        <span>{t('dashboard.paymentHistory.colSno', 'S.No')}</span>
                        <div className="ml-1">{getSortIcon('sno')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('invoice_number')}
                      >
                        <span>{t('dashboard.paymentHistory.colInvoice', 'Invoice #')}</span>
                        <div className="ml-1">{getSortIcon('invoice_number')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('invoice_date')}
                      >
                        <span>{t('dashboard.paymentHistory.colDate', 'Date')}</span>
                        <div className="ml-1">{getSortIcon('invoice_date')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colItems', 'Items')}
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('total_amount')}
                      >
                        <span>{t('dashboard.paymentHistory.colAmount', 'Amount')}</span>
                        <div className="ml-1">{getSortIcon('total_amount')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('payment_status')}
                      >
                        <span>{t('dashboard.paymentHistory.colStatus', 'Status')}</span>
                        <div className="ml-1">{getSortIcon('payment_status')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colActions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 sm:px-6 py-12 text-center">
                        <div className="flex flex-col items-center min-w-0">
                          <Receipt className="w-12 h-12 t-muted mb-4 flex-shrink-0" />
                          <h3 className="t-h3 t-muted mb-2 break-words text-center">{t('dashboard.paymentHistory.noInvoices', 'No Invoices')}</h3>
                          <p className="t-meta text-center">{t('dashboard.paymentHistory.noInvoicesDesc', 'No invoices found')}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getPaginatedData(sortData(invoices.map((inv, idx) => ({ ...inv, sno: idx + 1, payment_status: getEffectiveInvoiceStatus(inv) })), sortColumn)).map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">{invoice.sno}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-card-title">{invoice.invoice_number}</div>
                          <div className="t-meta" title={invoice.order_id}>{formatOrderRef(invoice.order_id)}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body">
                            {formatDate(invoice.invoice_date)}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body"><span className="t-num">{invoice.items.length}</span> {t('dashboard.paymentHistory.items', 'item(s)')}</div>
                          <div className="t-meta truncate max-w-[12rem] sm:max-w-xs">
                            {invoice.items[0]?.description
                              ? localizedInvoiceDescription(invoice.items[0].description, t)
                              : null}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">
                            {formatCurrency(invoice.total_amount)}
                          </div>
                          <div className="t-meta">{displayCurrencyCode()}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className={`ds-chip ${getStatusChipClass(getEffectiveInvoiceStatus(invoice))}`}>
                            {t(getStatusDisplayKey(getEffectiveInvoiceStatus(invoice)))}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleDownloadInvoice(invoice)}
                              disabled={downloadingInvoice === invoice.id}
                              className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
                              title={t('dashboard.paymentHistory.downloadPdf', 'Download PDF')}
                            >
                              {downloadingInvoice === invoice.id ? (
                                <LoadingSpinner size="xs" showText={false} />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEmailInvoice(invoice)}
                              disabled={emailingInvoice === invoice.id}
                              className="ds-btn ds-btn-secondary min-h-[44px] min-w-[44px] touch-manipulation"
                              title={t('dashboard.paymentHistory.emailInvoice', 'Email Invoice PDF')}
                            >
                              {emailingInvoice === invoice.id ? (
                                <LoadingSpinner size="xs" showText={false} />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {renderPagination(invoices.length)}
          </div>
        )}

        {/* Transactions — table only, no card border/bg */}
        {activeTab === 'transactions' && (
          <div className="rounded-xl overflow-hidden min-w-0">
            <p className="md:sr-only t-meta mb-2 px-0.5" aria-hidden="true">{t('dashboard.paymentHistory.swipeHint', 'Swipe table to see all columns')}</p>
            <div className="overflow-x-auto scrollbar-thin" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="ds-table min-w-[720px]">
                <thead>
                  <tr>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('sno')}
                      >
                        <span>{t('dashboard.paymentHistory.colSno', 'S.No')}</span>
                        <div className="ml-1">{getSortIcon('sno')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colReference', 'Reference')}
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <span>{t('dashboard.paymentHistory.colDate', 'Date')}</span>
                        <div className="ml-1">{getSortIcon('created_at')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('type')}
                      >
                        <span>{t('dashboard.paymentHistory.colType', 'Type')}</span>
                        <div className="ml-1">{getSortIcon('type')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('credits_amount')}
                      >
                        <span>{t('dashboard.paymentHistory.colCredits', 'Credits')}</span>
                        <div className="ml-1">{getSortIcon('credits_amount')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('amount')}
                      >
                        <span>{t('dashboard.paymentHistory.colAmount', 'Amount')}</span>
                        <div className="ml-1">{getSortIcon('amount')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('status')}
                      >
                        <span>{t('dashboard.paymentHistory.colStatus', 'Status')}</span>
                        <div className="ml-1">{getSortIcon('status')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colGateway', 'Gateway')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 sm:px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <CreditCard className="w-12 h-12 t-muted mb-4" />
                          <h3 className="t-h3 t-muted mb-2">{t('dashboard.paymentHistory.noTransactions', 'No Transactions')}</h3>
                          <p className="t-meta">{t('dashboard.paymentHistory.noTransactionsDesc', 'No transactions found')}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getPaginatedData(sortData(transactions.map((txn, idx) => ({ ...txn, sno: idx + 1 })), sortColumn)).map((txn) => (
                      <tr key={txn.id}>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">{txn.sno}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-mono" title={txn.order_id || txn.id}>
                            {txn.order_id ? formatOrderRef(txn.order_id) : formatTxnRef(txn.id)}
                          </div>
                          {orderIdToInvoiceNumber.get(txn.order_id) && (
                            <div className="t-meta t-link mt-0.5">{t('dashboard.paymentHistory.invoiceLabel', {
                              number: orderIdToInvoiceNumber.get(txn.order_id),
                              defaultValue: `Invoice: ${orderIdToInvoiceNumber.get(txn.order_id)}`,
                            })}</div>
                          )}
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body">
                            {formatDate(txn.created_at)}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body capitalize">{transactionTypeLabel(txn.type, t)}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">
                            {txn.credits_amount > 0 ? '+' : ''}{txn.credits_amount}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">
                            {formatCurrency(txn.amount)}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className={`ds-chip ${getStatusChipClass(getTransactionDisplayStatus(txn))}`}>
                            {t(getStatusDisplayKey(getTransactionDisplayStatus(txn)))}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body uppercase">
                            {txn.payment_gateway || t('dashboard.paymentHistory.notAvailable', 'N/A')}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {renderPagination(transactions.length)}
          </div>
        )}

        {/* Orders — table only, no card border/bg */}
        {activeTab === 'orders' && (
          <div className="rounded-xl overflow-hidden min-w-0">
            <p className="md:sr-only t-meta mb-2 px-0.5" aria-hidden="true">{t('dashboard.paymentHistory.swipeHint', 'Swipe table to see all columns')}</p>
            <div className="overflow-x-auto scrollbar-thin" style={{ WebkitOverflowScrolling: 'touch' }}>
              <table className="ds-table min-w-[640px]">
                <thead>
                  <tr>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('sno')}
                      >
                        <span>{t('dashboard.paymentHistory.colSno', 'S.No')}</span>
                        <div className="ml-1">{getSortIcon('sno')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colReference', 'Reference')}
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <span>{t('dashboard.paymentHistory.colDate', 'Date')}</span>
                        <div className="ml-1">{getSortIcon('created_at')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('credits')}
                      >
                        <span>{t('dashboard.paymentHistory.colCredits', 'Credits')}</span>
                        <div className="ml-1">{getSortIcon('credits')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('amount')}
                      >
                        <span>{t('dashboard.paymentHistory.colAmount', 'Amount')}</span>
                        <div className="ml-1">{getSortIcon('amount')}</div>
                      </button>
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      {t('dashboard.paymentHistory.colPaymentMethod', 'Payment Method')}
                    </th>
                    <th className="px-3 py-2 sm:px-6 sm:py-3 text-left t-label">
                      <button
                        type="button"
                        className="flex items-center space-x-1 hover:t-strong focus:outline-none"
                        onClick={() => handleSort('status')}
                      >
                        <span>{t('dashboard.paymentHistory.colStatus', 'Status')}</span>
                        <div className="ml-1">{getSortIcon('status')}</div>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 sm:px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <FileText className="w-12 h-12 t-muted mb-4" />
                          <h3 className="t-h3 t-muted mb-2">{t('dashboard.paymentHistory.noOrders', 'No Orders')}</h3>
                          <p className="t-meta">{t('dashboard.paymentHistory.noOrdersDesc', 'No orders found')}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    getPaginatedData(sortData(orders.map((order, idx) => ({ ...order, sno: idx + 1 })), sortColumn)).map((order) => {
                      const displayStatus = getOrderDisplayStatus(order);
                      const invoiceNum = orderIdToInvoiceNumber.get(order.order_id);
                      return (
                      <tr key={order.order_id}>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">{order.sno}</div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-mono" title={order.order_id}>
                            {formatOrderRef(order.order_id)}
                          </div>
                          {invoiceNum && <div className="t-meta t-link mt-0.5">{t('dashboard.paymentHistory.invoiceLabel', {
                            number: invoiceNum,
                            defaultValue: `Invoice: ${invoiceNum}`,
                          })}</div>}
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body">
                            {formatDate(order.created_at)}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">
                            {order.credits}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body t-num">
                            {formatCurrency(order.amount)}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className="t-body uppercase">
                            {order.payment_method}
                          </div>
                        </td>
                        <td className="px-3 py-3 sm:px-6 sm:py-4 align-middle">
                          <div className={`ds-chip ${getStatusChipClass(displayStatus)}`}>
                            {t(getStatusDisplayKey(displayStatus))}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {renderPagination(orders.length)}
          </div>
        )}

        {/* ── Usage / Consumption panel ─────────────────────────────────── */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="t-h3">{t('dashboard.paymentHistory.featureConsumption', 'Feature Consumption')}</h3>
                <p className="t-meta mt-0.5">
                  {t('dashboard.paymentHistory.featureConsumptionDesc', 'Internal usage metrics — shown for transparency on invoices. No additional charges.')}
                </p>
              </div>
              <div className="flex gap-1">
                {(['7d', '30d', '90d'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setUsagePeriod(p)}
                    className={`ds-btn ds-btn-sm ${
                      usagePeriod === p
                        ? 'ds-btn-primary'
                        : 'ds-btn-secondary'
                    }`}
                  >
                    {p === '7d'
                      ? t('dashboard.paymentHistory.period7d', 'Last 7 days')
                      : p === '30d'
                        ? t('dashboard.paymentHistory.period30d', 'Last 30 days')
                        : t('dashboard.paymentHistory.period90d', 'Last 90 days')}
                  </button>
                ))}
              </div>
            </div>

            {usageRows.length === 0 ? (
              <div className="text-center py-12 t-muted">
                <BarChart2 className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p className="t-body">{t('dashboard.paymentHistory.usageEmpty', 'No feature usage recorded in this period.')}</p>
                <p className="t-meta mt-1">{t('dashboard.paymentHistory.usageEmptyHint', 'Usage is tracked each time you optimise a resume, search for jobs, or use AI features.')}</p>
              </div>
            ) : (
              (() => {
                // Group by category
                const byCategory = usageRows.reduce<Record<string, UsageSummaryRow[]>>((acc, row) => {
                  const cat = row.category ?? 'other';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(row);
                  return acc;
                }, {});

                return (
                  <div className="space-y-4">
                    {Object.entries(byCategory).map(([category, rows]) => {
                      const catLabel = categoryLabel(category, t);
                      return (
                        <div key={category} className="ds-card-flush">
                          {/* Category header */}
                          <div className="ds-recessed px-4 py-2.5 flex items-center justify-between">
                            <span className="t-label">{catLabel}</span>
                            <span className="t-meta t-num">
                              {t('dashboard.paymentHistory.events', {
                                count: rows.reduce((s, r) => s + Number(r.event_count), 0),
                                defaultValue: `${rows.reduce((s, r) => s + Number(r.event_count), 0)} events`,
                              })}
                            </span>
                          </div>
                          {/* Operations table */}
                          <div className="divide-y divide-[color:var(--ds-divider)]">
                            {rows.map((row) => {
                              const opLabel = operationLabel(row.operation, t);
                              const maxUnits = Math.max(...rows.map(r => Number(r.total_units)));
                              const pct = maxUnits > 0 ? Math.round((Number(row.total_units) / maxUnits) * 100) : 0;
                              return (
                                <div key={row.operation} className="px-4 py-3 flex items-center gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="t-body truncate">{opLabel}</div>
                                    <div className="ds-progress mt-1.5">
                                      <div
                                        className="ds-progress-fill"
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  </div>
                                  <div className="text-right shrink-0">
                                    <div className="t-body t-num">
                                      {Number(row.total_units).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </div>
                                    <div className="t-meta t-num">{t('dashboard.paymentHistory.events', {
                                      count: Number(row.event_count),
                                      defaultValue: `${Number(row.event_count)} events`,
                                    })}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentHistoryPage;
